/* Rendering an answer as the shape it is, not as a wall of paragraphs.
 *
 * Bullets stay a list, a procedure becomes a numbered track, figures become a table the
 * reader can scan down a column — and where that table is unambiguously chartable, a chart
 * of its own numbers can be shown beside it.
 *
 * Every string here is rendered as JSX children, never as an HTML string: document text
 * containing `<`, `&` or quotes is escaped by React and cannot become markup.
 */

import { useMemo, useState, type ReactNode } from 'react'

import { AnswerChart } from './AnswerChart'
import {
  chartableFrom,
  isNumericColumn,
  parseAnswer,
  type AnswerBlock,
} from './answerFormat'
import { Icon } from './icons'

/** `[3]` — a verified citation's display rank, written into the prose by the server. */
const MARKER = /\[(\d+)\]/g
const BOLD = /\*\*(.+?)\*\*/g

/**
 * Inline text: **bold** and clickable citation markers.
 *
 * The marker is the reader's one route from a claim to the passage behind it, so it is a
 * button rather than decoration — pressing it opens that source. Split with a single pass
 * so bold spanning a marker cannot swallow it.
 */
export function Inline({
  text,
  onCite,
}: {
  text: string
  onCite?: (rank: number) => void
}) {
  const nodes: ReactNode[] = []
  let key = 0

  // Bold first: its delimiters never appear inside a marker.
  const segments = text.split(BOLD)
  segments.forEach((segment, index) => {
    if (index % 2 === 1) {
      nodes.push(<strong key={`b${(key += 1)}`}>{segment}</strong>)
      return
    }
    let cursor = 0
    MARKER.lastIndex = 0
    let match: RegExpExecArray | null
    while ((match = MARKER.exec(segment)) !== null) {
      if (match.index > cursor) nodes.push(segment.slice(cursor, match.index))
      const rank = Number.parseInt(match[1] ?? '', 10)
      nodes.push(
        onCite ? (
          <button
            key={`m${(key += 1)}`}
            type="button"
            className="cite-marker"
            title={`Open source ${rank}`}
            aria-label={`Open source ${rank}`}
            onClick={() => onCite(rank)}
          >
            {rank}
          </button>
        ) : (
          <sup key={`m${(key += 1)}`} className="cite-marker static">
            {rank}
          </sup>
        ),
      )
      cursor = match.index + match[0].length
    }
    if (cursor < segment.length) nodes.push(segment.slice(cursor))
  })

  return <>{nodes}</>
}

/** A whole-paragraph _italic_ convention, kept from the prototype. */
function Paragraph({ text, onCite }: { text: string; onCite?: (rank: number) => void }) {
  const italic = /^_(.*)_$/.exec(text)
  const body = italic?.[1] ?? text
  const nodes = <Inline text={body} onCite={onCite} />
  return <p>{italic ? <em>{nodes}</em> : nodes}</p>
}

/** An ordered list is a procedure: numbered track, one step per row. */
function Steps({ items, onCite }: { items: string[]; onCite?: (rank: number) => void }) {
  return (
    <ol className="ans-steps">
      {items.map((item, i) => (
        <li key={i}>
          <span className="ans-step-n" aria-hidden="true">
            {i + 1}
          </span>
          <span className="ans-step-t">
            <Inline text={item} onCite={onCite} />
          </span>
        </li>
      ))}
    </ol>
  )
}

function TableBlock({
  block,
  onCite,
}: {
  block: Extract<AnswerBlock, { kind: 'table' }>
  onCite?: (rank: number) => void
}) {
  const chartable = useMemo(() => chartableFrom(block), [block])
  const numeric = useMemo(
    () => block.head.map((_, i) => (i === 0 ? false : isNumericColumn(block.rows, i))),
    [block],
  )
  // The table is the default view. A chart is an option on top of it, never instead of it.
  const [showChart, setShowChart] = useState(false)

  return (
    <div className="ans-figure">
      {chartable && (
        <div className="ans-figure-bar">
          {/* The measure, not the label column — repeating "Year ended" above a column
              already headed "Year ended" tells the reader nothing. */}
          <span className="ans-figure-h">
            {block.head.slice(1).filter(Boolean).join(' · ') || 'Figures'}
          </span>
          <div className="ans-toggle" role="group" aria-label="View as">
            <button
              type="button"
              className={showChart ? '' : 'on'}
              aria-pressed={!showChart}
              onClick={() => setShowChart(false)}
            >
              <Icon name="list" size={13} /> Table
            </button>
            <button
              type="button"
              className={showChart ? 'on' : ''}
              aria-pressed={showChart}
              onClick={() => setShowChart(true)}
            >
              <Icon name="chart" size={13} /> Chart
            </button>
          </div>
        </div>
      )}

      {chartable && showChart ? (
        <AnswerChart data={chartable} />
      ) : (
        <div className="ans-table-wrap">
          <table className="ans-table">
            <thead>
              <tr>
                {block.head.map((cell, i) => (
                  <th key={i} className={numeric[i] ? 'num' : undefined} scope="col">
                    <Inline text={cell} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c} className={numeric[c] ? 'num' : undefined}>
                      <Inline text={cell} onCite={onCite} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export function AnswerBlocks({
  blocks,
  onCite,
}: {
  blocks: AnswerBlock[]
  onCite?: (rank: number) => void
}) {
  return (
    <>
      {blocks.map((block, i) => {
        switch (block.kind) {
          case 'p':
            return <Paragraph key={i} text={block.text} onCite={onCite} />
          case 'ul':
            return (
              <ul key={i} className="ans-list">
                {block.items.map((item, j) => (
                  <li key={j}>
                    <Inline text={item} onCite={onCite} />
                  </li>
                ))}
              </ul>
            )
          case 'ol':
            return <Steps key={i} items={block.items} onCite={onCite} />
          case 'quote':
            return (
              <blockquote key={i} className="ans-quote">
                <Inline text={block.text} onCite={onCite} />
              </blockquote>
            )
          case 'table':
            return <TableBlock key={i} block={block} onCite={onCite} />
          default:
            return null
        }
      })}
    </>
  )
}

/** Parse and render in one step, for callers that hold raw answer text. */
export function AnswerBody({ content, onCite }: { content: string; onCite?: (rank: number) => void }) {
  const blocks = useMemo(() => parseAnswer(content), [content])
  return <AnswerBlocks blocks={blocks} onCite={onCite} />
}

export { parseAnswer }
